import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { AppState } from 'src';

import { dateParser } from '@common/dateParser';
import { SavedCreditCard, AccountViewModel } from '@models/Account';
import { OptionsViewModel } from '@models/common.models';
import { PaymentTerms, PaymentType, PaymentViewModel } from '@models/Payment';

import { PaymentComponent, PaymentComponentProperties } from './hosted-payment.component';
import { PaymentPurpose } from '@models/Payment';
import { ContractOrderType } from '@models/Contract';
import {
  setPaymentAmountInitial,
  setPaymentDate,
  setPaymentTerms,
  setPaymentType,
  setSavedCreditCard
} from '../../payment.actions';

export interface PaymentContainerProperties {
  readonly affiliatedAccounts?: OptionsViewModel[];
  readonly contractEndDate?: Date;
  readonly contractId?: number;
  readonly defaultSavedCreditCard?: SavedCreditCard;
  readonly isBalancePayment?: boolean;
  readonly isDemandLetterDefault?: boolean;
  readonly isDemandLetterEnabled?: boolean;
  readonly orderType?: ContractOrderType;
  readonly paymentPurpose: PaymentPurpose;
  readonly savedCreditCards?: Array<SavedCreditCard>;
  readonly swoId?: number;
  readonly totalPrice?: number;
  readonly submit: (model: PaymentViewModel) => void;
  readonly submitButtonRef?: (button: HTMLButtonElement) => void;
}

// This is used to translate the state of the page to the props on the component
function mapStateToProps(state: AppState, ownProps: PaymentContainerProperties): Partial<PaymentComponentProperties> {
  return {
    affiliatedAccounts: ownProps.affiliatedAccounts,
    contractEndDate: ownProps.contractEndDate,
    defaultSavedCreditCard: ownProps.defaultSavedCreditCard,
    installmentsFee: state.payment.installmentsFee,
    isDemandLetterDefault: ownProps.isDemandLetterDefault,
    isDemandLetterEnabled: ownProps.isDemandLetterEnabled,
    isPaymentDateInvalid: state.payment.isPaymentDateInvalid,
    maxPaymentAmountInitial: state.payment.maxPaymentAmountInitial,
    orderType: ownProps.orderType,
    paymentAmountBalance: state.payment.paymentAmountBalance,
    paymentAmountInitial: state.payment.paymentAmountInitial,
    paymentDate: state.payment.paymentDate,
    paymentPerTermString: state.payment.paymentPerTermString,
    paymentPurpose: ownProps.paymentPurpose,
    paymentTerms: state.payment.paymentTerms,
    paymentTypeBalance: state.payment.paymentTypeBalance,
    paymentTypeInitial: state.payment.paymentTypeInitial,
    remainingBalance: state.payment.remainingBalance,
    remainingBalanceDueString: state.payment.remainingBalanceDueString,
    savedCreditCardBalance: state.payment.savedCreditCardBalance,
    savedCreditCardInitial: state.payment.savedCreditCardInitial,
    savedCreditCards: ownProps.savedCreditCards,
    totalBalanceDueString: state.payment.totalBalanceDueString,
    totalPrice: ownProps.totalPrice
  };
}

// This should create proxy wrappers for actions used by the component
function mapDispatchToProps(
  dispatch: Dispatch<AppState>,
  ownProps: PaymentContainerProperties
): Partial<PaymentComponentProperties> {
  return {
    setPaymentAmountInitial: (paymentAmount: number | null) =>
      dispatch(setPaymentAmountInitial(paymentAmount, ownProps.totalPrice!, ownProps.orderType!)),
    setPaymentDate: (paymentDate: Date, contractEndDate?: Date) =>
      dispatch(setPaymentDate(paymentDate, contractEndDate)),
    setPaymentTerms: (paymentTerms: PaymentTerms) =>
      dispatch(setPaymentTerms(paymentTerms, ownProps.totalPrice!, ownProps.orderType!)),
    setPaymentType: (paymentType: PaymentType, isBalance: boolean) =>
      dispatch(setPaymentType(paymentType, ownProps.totalPrice!, isBalance, ownProps.orderType!)),
    setSavedCreditCard: (creditCard: SavedCreditCard | undefined, isBalance: boolean) =>
      dispatch(setSavedCreditCard(creditCard, isBalance)),
    submit: (model: PaymentViewModel, copyInitialPaymentDetails: boolean, savedCreditCardInitial?: SavedCreditCard) => {
      if (copyInitialPaymentDetails) {
        model.paymentCreditCardBalance = model.paymentCreditCardInitial;
      }
      if (savedCreditCardInitial != null) {
        const account: AccountViewModel = savedCreditCardInitial.account;
        model.paymentCreditCardInitial = {
          creditCardType: account.creditCardType,
          expirationMonth:
            account.creditCardExpirationDate == null
              ? undefined
              : +dateParser
                  .parse(account.creditCardExpirationDate)
                  .toDate()
                  .getMonth() + 1,
          expirationYear:
            account.creditCardExpirationDate == null
              ? undefined
              : +dateParser
                  .parse(account.creditCardExpirationDate)
                  .toDate()
                  .getFullYear()
                  .toString()
                  .slice(2, 4)
                  .toString(),
          nameOnCard: account.creditCardOwnerName,
          token: account.creditCardToken
        };
      }
      ownProps.submit(model);
    },
    submitButtonRef: ownProps.submitButtonRef
  };
}

// Connect function "wires" both of the map functions and returns a wrapper container for the component
export const HostedPayment = connect(
  mapStateToProps,
  mapDispatchToProps
)(PaymentComponent);



// WEBPACK FOOTER //
// ./src/payment/_components/gateway/hosted-payment.container.ts